import { useState } from 'react'
import { useDefinirPapeis } from '../../../api/queries/usuarios'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import type { UsuarioAdministrado } from '../../../types/usuario'
import { SelecaoDePapeis } from './SelecaoDePapeis'

/**
 * Alterar os papéis de uma conta que já existe.
 *
 * **Faltava, e a falta aparecia na primeira semana de qualquer teste com gente de verdade.** Até
 * aqui só dava para definir papel no cadastro: uma conta criada com o papel errado — ou uma pessoa
 * que passou a integrar o comitê — só se corrigia recriando a conta, o que troca o identificador e
 * deixa a trilha de auditoria apontando para um cadastro morto.
 *
 * O conjunto é substituído inteiro, não incrementado. É o contrato do `PUT .../papeis`, e é o certo:
 * conceder e revogar pela mesma tela, com o estado final à vista, evita a operação que ninguém
 * lembra de fazer — a revogação.
 *
 * Deixar sem papel nenhum é permitido de propósito: é o jeito de tirar acesso de alguém sem
 * desativar a conta, e o servidor trata quem não tem papel como somente consulta.
 *
 * @param props a conta e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDePapeis({ conta, aoFechar }: { conta: UsuarioAdministrado; aoFechar: () => void }) {
  const definir = useDefinirPapeis(conta.id)
  const [papeis, setPapeis] = useState(conta.papeis)

  async function salvar() {
    try {
      await definir.mutateAsync(papeis)
      aoFechar()
    } catch {
      // O AvisoDeErro abaixo mostra o que o servidor recusou.
    }
  }

  return (
    <Dialogo titulo={`Papéis de ${conta.nome}`} aoFechar={aoFechar}>
      <div className="space-y-4">
        <p className="max-w-prose text-sm text-slate-600">{TEXTOS.usuarios.alterarPapeis.ajuda}</p>

        <SelecaoDePapeis
          papeis={papeis}
          aoAlterar={setPapeis}
          daContratada={conta.papelContratual === 'CONTRATADA'}
          desabilitado={definir.isPending}
        />

        {papeis.length === 0 && (
          <p className="max-w-prose rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {TEXTOS.usuarios.alterarPapeis.semPapelNenhum}
          </p>
        )}

        {definir.isError && <AvisoDeErro erro={definir.error} titulo={TEXTOS.usuarios.naoSalvou} />}

        <div className="flex flex-wrap gap-2">
          <Botao aparencia="primario" onClick={salvar} disabled={definir.isPending}>
            {definir.isPending ? 'Salvando…' : 'Salvar papéis'}
          </Botao>
          <Botao onClick={aoFechar} disabled={definir.isPending}>
            Cancelar
          </Botao>
        </div>
      </div>
    </Dialogo>
  )
}
